import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, IconButton, InputAdornment, OutlinedInput, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import RankBadge from '../gameScoreBoard/RankBadge';
import Pagination from '../userAnalytics/Pagination';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { getQuizParticipation } from '../../api/gameScheduleApi';
import { C, entityColor } from '../../theme/adminTokens';
import { analyticsUiTokens as T } from '../../constants/userAnalytics';
import {
  PARTICIPANT_DEFAULT_PAGE_SIZE, PARTICIPANT_PAGE_SIZES, PARTICIPANT_SEARCH_MAX_LENGTH,
} from '../../constants/gameSchedule';
import { initialOf } from '../../utils/avatarColor';
import { formatPeriodRange } from '../../utils/gameSchedule';

const TITLE_ID = 'quiz-participants-title';
const SEARCH_DEBOUNCE_MS = 300;

const thinScrollbar = {
  scrollbarWidth: 'thin',
  scrollbarColor: '#c7d8ec transparent',
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#c7d8ec', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: T.slate[400] },
};

const headCellSx = {
  fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, '&:last-of-type': { borderRight: 'none' },
  whiteSpace: 'nowrap', py: 1.5, px: 2, bgcolor: '#f1f5f9',
};

const bodyCellSx = {
  fontSize: 13, color: '#1e293b', borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' }, py: 1.25, px: 2, verticalAlign: 'middle',
};

const fmtParticipated = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const fmtCount = (n) => Number(n ?? 0).toLocaleString('en-US');

/**
 * View Participants (plan 13.5): one row per fan who completed the quiz — rank inside the quiz, player, e-mail, correct
 * answers, the score of this quiz, the running monthly total and the date. Search by username or e-mail, paging; data
 * from GET /cms/quiz/participation. The popup mounts fresh each time it opens, so search and page start over.
 */
export default function QuizParticipantsModal({ quiz, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PARTICIPANT_DEFAULT_PAGE_SIZE);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [reloadKey, setReloadKey] = useState(0);

  const search = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS, searchInput.trim() === '');
  const searching = search !== '';

  const searchLabel = useLocalText('Search by username or email…');
  const clearLabel = useLocalText('Clear search');
  const rowsLabel = useLocalText('Rows per page');
  const closeLabel = useLocalText('Close');

  const quizId = quiz?.id;
  const controllerRef = useRef(null);

  useEffect(() => {
    if (quizId == null) return undefined;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus('loading');

    getQuizParticipation(quizId, { search, page, pageSize }, { signal: controller.signal, _silent: true })
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result ?? { items: [], totalCount: 0 });
        setStatus('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setStatus('error');
      });

    return () => controller.abort();
  }, [quizId, search, page, pageSize, reloadKey]);

  const retry = useCallback(() => {
    setStatus('loading');
    setReloadKey((k) => k + 1);
  }, []);

  const items = data?.items ?? [];
  const total = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstLoad = !data && status === 'loading';
  const refreshing = Boolean(data) && status === 'loading';
  const shownPage = data?.pageNumber ?? page;
  const shownSize = data?.pageSize ?? pageSize;
  const from = total === 0 ? 0 : (shownPage - 1) * shownSize + 1;
  const to = Math.min(shownPage * shownSize, total);

  const handleSearchChange = (value) => { setSearchInput(value); setPage(1); };
  const handlePageSize = (value) => { setPageSize(value); setPage(1); };

  const range = formatPeriodRange(quiz?.startDate, quiz?.endDate);

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      aria-labelledby={TITLE_ID}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(2px)' } },
        paper: {
          'data-testid': 'participants-modal',
          sx: {
            width: '100%', maxWidth: 1320, height: fullScreen ? '100%' : 'min(90vh, 800px)', maxHeight: fullScreen ? '100%' : '90vh',
            m: { xs: 0, sm: 2 }, borderRadius: fullScreen ? 0 : '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          },
        },
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.75, sm: 2 }, bgcolor: C.navy, color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: T.sky[300] }}>
            <EmojiEventsOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography id={TITLE_ID} component="h2" noWrap sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {quiz?.name} — <LocalText>Participants</LocalText>
            </Typography>
            <Typography data-testid="participants-subtitle" noWrap sx={{ fontSize: 11, color: T.slate[300], mt: 0.25 }}>
              {[range, data ? `${fmtCount(total)} ${total === 1 ? 'participant' : 'participants'}` : null].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label={closeLabel} sx={{ width: 32, height: 32, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.1)', color: T.slate[200], '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', color: '#ffffff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: T.slate[50], borderBottom: `1px solid ${T.cardBorder}`, flexShrink: 0 }}>
        <OutlinedInput
          size="small"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={searchLabel}
          inputProps={{ 'aria-label': searchLabel, maxLength: PARTICIPANT_SEARCH_MAX_LENGTH, autoComplete: 'off' }}
          startAdornment={<InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 18, color: T.slate[400] }} /></InputAdornment>}
          endAdornment={searchInput ? (
            <InputAdornment position="end">
              <IconButton size="small" edge="end" aria-label={clearLabel} onClick={() => handleSearchChange('')} sx={{ color: T.slate[400] }}>
                <CancelIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : null}
          sx={{ width: '100%', maxWidth: { sm: 420 }, bgcolor: '#ffffff', borderRadius: '8px', fontSize: 13, '& fieldset': { borderColor: T.slate[200] }, '&.Mui-focused fieldset': { borderColor: `${T.navy} !important`, borderWidth: '1px !important' } }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: '#ffffff', ...thinScrollbar }}>
        {status === 'error' && (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={retry}><LocalText>Retry</LocalText></Button>}
              sx={{ borderRadius: 2 }}
            >
              <LocalText>Couldn't load the participants.</LocalText>
            </Alert>
          </Box>
        )}
        {!data ? (
          firstLoad && (
            <Box role="status" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} aria-label="Loading participants" />
            </Box>
          )
        ) : items.length === 0 ? (
          <Box data-testid="participants-empty" role="status" sx={{ py: 6, px: 2, textAlign: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: T.slate[100], color: T.slate[400], display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
              {searching ? <SearchOutlinedIcon sx={{ fontSize: 26 }} /> : <EmojiEventsOutlinedIcon sx={{ fontSize: 26 }} />}
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.slate[700] }}>
              <LocalText>{searching ? 'No matching participants found' : 'No one has played this quiz yet.'}</LocalText>
            </Typography>
            {searching && (
              <Typography sx={{ fontSize: 12, color: T.slate[500], mt: 0.5 }}>
                <LocalText>Try a different name or e-mail, or clear the search box.</LocalText>
              </Typography>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ opacity: refreshing ? 0.5 : 1, transition: 'opacity 0.15s' }}>
            <Table size="small" aria-label="Quiz participants" sx={{ minWidth: 760, '& tbody tr:last-of-type td': { borderBottom: 'none' } }}>
              <TableHead>
                <TableRow>
                  <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Rank</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx}><LocalText>Player</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx}><LocalText>Email Address</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Correct</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Score</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Month Total</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Participated</LocalText></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row, idx) => (
                  <TableRow key={`${row.userId}-${row.gameScheduleId}`} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}><RankBadge rank={row.rank} /></Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box sx={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: entityColor(idx), color: '#ffffff', fontSize: 11.5, fontWeight: 700 }}>
                          {initialOf(row.username || row.email)}
                        </Box>
                        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>{row.username}</Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, overflowWrap: 'anywhere' }}>{row.email || '—'}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">{row.correctCount} / {row.totalQuestions}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="center">{row.weeklyScore}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">{row.monthlyScore}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: C.muted }} />
                        {fmtParticipated(row.participatedAt)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: T.slate[50], borderTop: `1px solid ${T.cardBorder}`, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'space-between', md: 'flex-start' } }}>
          <Box component="label" htmlFor="quiz-rows-per-page" sx={{ fontSize: 11, fontWeight: 600, color: T.slate[500] }}>
            <LocalText>Rows per page:</LocalText>
          </Box>
          <Select
            native
            size="small"
            value={pageSize}
            onChange={(e) => handlePageSize(Number(e.target.value))}
            inputProps={{ id: 'quiz-rows-per-page', 'aria-label': rowsLabel }}
            sx={{ bgcolor: '#ffffff', borderRadius: '8px', fontSize: 12, fontWeight: 600, color: T.navy, '& .MuiSelect-select': { py: '5px' }, '& fieldset': { borderColor: T.slate[200] } }}
          >
            {PARTICIPANT_PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
        </Box>
        <Typography data-testid="participants-summary" sx={{ fontSize: 12, fontWeight: 500, color: T.slate[500], textAlign: 'center' }}>
          <LocalText>Showing</LocalText>{' '}
          <Box component="strong" sx={{ color: T.slate[800] }}>{total === 0 ? '0' : `${fmtCount(from)}–${fmtCount(to)}`}</Box>{' '}
          <LocalText>of</LocalText>{' '}
          <Box component="strong" sx={{ color: T.slate[800] }}>{fmtCount(total)}</Box>{' '}
          <LocalText>participants</LocalText>
        </Typography>
        {total > 0 ? <Pagination page={page} totalPages={totalPages} onChange={setPage} /> : <Box />}
      </Box>
    </Dialog>
  );
}
